/**
 * Unified Calendar Types
 * Supports manual events, invoice due dates, and recurring invoices
 */

import { CalendarEvent, Job } from '../pages/job-summary/Interfaces';
import { XeroInvoice } from './xero';
import { RepeatingInvoice } from './accounting';
import { parseXeroDate } from '../utils/xeroDate';
import { addFrequency } from '../utils/recurrence';

// Calendar item types for visual differentiation
export type CalendarItemType = 'event' | 'invoice_due' | 'invoice_created' | 'recurring_invoice' | 'recurring_expense' | 'bill_due' | 'job_due';

/**
 * Which layout the calendar renders.
 *
 * Declared here rather than in the grid component so the route (which parses it
 * out of `?view=`), the toolbar switcher and the grid all import ONE definition —
 * a second copy is how the filter shape drifted apart before.
 *
 * There is deliberately no 'day': the day detail panel already IS the day view,
 * and it opens from every other view.
 */
export type CalendarView = 'month' | 'week' | 'agenda';

// Color constants for each item type.
//
// These MUST stay in step with the $cal-*-color SCSS variables at the top of
// Calendar.scss. The chips take their colour from the SCSS classes, but the day
// panel's section dots read these constants directly — so when the two diverge
// the dot and the chip for the same item render different colours.
//
// They are all opaque. The recurring/expense/bill entries used to carry 0.8-0.9
// alpha, which meant the effective colour was a composite of the swatch and
// whatever canvas sat behind it: it changed per theme, and under white chip text
// the green composited to roughly 1.9:1 on the Graphite canvas. Each is now one
// step darker so the contrast holds on any canvas.
export const CALENDAR_ITEM_COLORS = {
  event: '#3b82f6',           // Blue for events
  invoice_due: '#0891b2',     // Darker cyan for invoice due dates
  invoice_created: '#6366f1', // Indigo for invoice creation dates
  recurring_invoice: '#15803d', // Green for recurring invoices (income)
  recurring_expense: '#dc2626', // Red for recurring expenses (scheduled)
  bill_due: '#991b1b',        // Darker red for actual bills/expenses
  job_due: '#b45309',         // Amber for job due dates — the only free slot left between the blues/greens/reds
} as const;

// Icons for each item type (Bootstrap Icons) — recurring templates share the same icon
export const CALENDAR_ITEM_ICONS = {
  event: 'bi-calendar-event',
  invoice_due: 'bi-currency-dollar',
  invoice_created: 'bi-file-earmark-plus',
  recurring_invoice: 'bi-arrow-repeat',
  recurring_expense: 'bi-arrow-repeat',
  bill_due: 'bi-receipt',
  job_due: 'bi-briefcase',
} as const;

/**
 * Unified calendar item that can represent an event, invoice, or recurring invoice
 */
export interface UnifiedCalendarItem {
  id: string;
  type: CalendarItemType;
  title: string;
  start: string;
  end?: string;
  allDay?: boolean;
  color: string;
  icon?: string;
  
  // Source data references (only one will be populated)
  eventData?: CalendarEvent;
  invoiceData?: XeroInvoice;
  repeatingInvoiceData?: RepeatingInvoice;
  jobData?: Job;
  /** Source identity for read-only support/payment overlays; never an events ID. */
  commitment?: CalendarCommitmentSource;

  // Display metadata
  customerName?: string;
  customerId?: string;
  amount?: number;
  currencyCode?: string;
  description?: string;

  // For recurring items - identifies which occurrence this is
  occurrenceDate?: string;
  isOccurrence?: boolean;
  /**
   * For a recurring EVENT occurrence: the id of the base CalendarEvent document.
   * `id` is unique per occurrence (`event-<id>-<date>`), so anything that needs
   * to read or write the underlying Firestore document must use this instead —
   * editing/deleting via the occurrence id used to rewrite or wipe the whole
   * series from its original date.
   */
  baseEventId?: string;
}

export interface CalendarCommitmentSource {
  kind: 'support' | 'stripe';
  planId?: string; month?: string; jobId?: string; subscriptionId?: string;
  completed?: boolean; planned?: boolean; estimated?: boolean;
  ownerName?: string; completedDuties?: number; totalDuties?: number;
}

/**
 * Filter state for the unified calendar
 */
export interface CalendarFilterState {
  showEvents: boolean;
  showInvoices: boolean;
  invoiceDateMode: 'due' | 'created';
  showRecurringInvoices: boolean;
  /** Sub-toggle: show recurring invoice templates (templateType === 'invoice') */
  recurringShowInvoices: boolean;
  /** Sub-toggle: show recurring bill templates (templateType === 'bill') */
  recurringShowBills: boolean;
  showRecurringExpenses: boolean;
  /** Show open jobs on their due date (same source + open-test as TodayTimeline) */
  showJobs: boolean;
  showSupport?: boolean;
  showCompletedSupport?: boolean;
  showStripePayments?: boolean;
}

/**
 * Default filter state - show all items
 */
export const DEFAULT_CALENDAR_FILTERS: CalendarFilterState = {
  showEvents: true,
  showInvoices: true,
  invoiceDateMode: 'due',
  showRecurringInvoices: true,
  recurringShowInvoices: true,
  recurringShowBills: true,
  showRecurringExpenses: true,
  showJobs: true,
  showSupport: true,
  showCompletedSupport: false,
  showStripePayments: true,
};

/**
 * Convert a date to local YYYY-MM-DD (timezone-safe for all-day items).
 */
export function toLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Normalise any stored date value to 'YYYY-MM-DD', or null if unparseable.
 * A bare 'YYYY-MM-DD' round-trips unchanged (the canonical parser reads it as
 * local midnight), so this is safe on both plain days and full ISO instants.
 */
function toDayString(value: unknown): string | null {
  const d = parseXeroDate(value);
  return d ? toLocalDateString(d) : null;
}

/**
 * 'YYYY-MM-DD' -> the UTC-midnight ISO instant. Kept identical to what this file
 * has always emitted for all-day recurring occurrences so downstream bucketing
 * is unaffected: in Adelaide (+9:30/+10:30) UTC midnight is 09:30/10:30 the same
 * local day, so the local calendar day is preserved.
 */
function dayToIso(day: string): string {
  return new Date(`${day}T00:00:00Z`).toISOString();
}

/**
 * Whole days between two 'YYYY-MM-DD' strings (b - a).
 * Measured at UTC-noon so no DST shift can round the difference off by one.
 */
function dayDiff(a: string, b: string): number {
  const ms = new Date(`${b}T12:00:00Z`).getTime() - new Date(`${a}T12:00:00Z`).getTime();
  return Math.round(ms / 86400000);
}

/**
 * Helper function to create a calendar item from an event.
 *
 * @param occurrenceDate Optional 'YYYY-MM-DD'. Supply it when materialising one
 *   occurrence of a RECURRING event. Every occurrence used to carry the base
 *   document's id, so editing the 5th occurrence rewrote the series from its
 *   original date and deleting it wiped the series. An occurrence therefore
 *   gets its own composite id plus `baseEventId` pointing at the real document,
 *   and its start/end are moved onto the occurrence day (same wall-clock time,
 *   same duration in days) — shifting whole days with local Date arithmetic is
 *   DST-safe, which matters on Adelaide's half-hour offset.
 *   Omitted, the returned item is byte-identical to what it has always been.
 */
export function eventToCalendarItem(event: CalendarEvent, occurrenceDate?: string): UnifiedCalendarItem {
  const normalizedStart = event.allDay
    ? toLocalDateString(new Date(event.start))
    : event.start;
  const normalizedEnd = event.end
    ? (event.allDay ? toLocalDateString(new Date(event.end)) : event.end)
    : undefined;

  const base: UnifiedCalendarItem = {
    id: `event-${event.id}`,
    type: 'event',
    title: event.title,
    start: normalizedStart,
    end: normalizedEnd,
    allDay: event.allDay,
    color: event.colour || CALENDAR_ITEM_COLORS.event,
    icon: CALENDAR_ITEM_ICONS.event,
    eventData: event,
    description: event.description,
  };

  if (!occurrenceDate) return base;

  const startDate = new Date(event.start);
  if (isNaN(startDate.getTime())) return base;
  const shiftDays = dayDiff(toLocalDateString(startDate), occurrenceDate);

  const shifted = new Date(startDate);
  shifted.setDate(shifted.getDate() + shiftDays); // local: preserves wall-clock across DST

  let shiftedEnd: string | undefined;
  if (event.end) {
    const endDate = new Date(event.end);
    if (!isNaN(endDate.getTime())) {
      const e = new Date(endDate);
      e.setDate(e.getDate() + shiftDays);
      shiftedEnd = event.allDay ? toLocalDateString(e) : e.toISOString();
    }
  }

  return {
    ...base,
    id: `event-${event.id}-${occurrenceDate}`,
    start: event.allDay ? toLocalDateString(shifted) : shifted.toISOString(),
    end: shiftedEnd,
    isOccurrence: true,
    occurrenceDate,
    baseEventId: event.id,
  };
}

/**
 * Parse a Xero date.
 *
 * Delegates to the canonical `src/app/utils/xeroDate.ts` implementation — this
 * file used to carry its own copy, one of five in the repo with three different
 * behaviours. The canonical one additionally reads a bare 'YYYY-MM-DD' as LOCAL
 * midnight instead of UTC midnight, which is what stops a plain Xero due date
 * landing on the previous day. Kept exported under the old name so existing
 * imports keep compiling.
 */
export function parseXeroDateForCalendar(dateStr: string | undefined): Date | null {
  return parseXeroDate(dateStr);
}

/**
 * Helper function to create a calendar item from an invoice due date
 */
export function invoiceToCalendarItem(invoice: XeroInvoice): UnifiedCalendarItem | null {
  const dueDate = invoice.DueDate || invoice.DueDateString;
  if (!dueDate) return null;

  // Was a sixth inline copy of the Xero date parser; use the canonical one.
  const parsedDate = parseXeroDateForCalendar(dueDate);
  if (!parsedDate) return null;

  const formattedAmount = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: invoice.CurrencyCode || 'AUD',
  }).format(invoice.AmountDue);
  
  return {
    id: `invoice-${invoice.InvoiceID}`,
    type: 'invoice_due',
    title: `${invoice.InvoiceNumber} - ${invoice.Contact?.Name || 'Unknown'} (${formattedAmount})`,
    start: parsedDate.toISOString(),
    allDay: true,
    color: CALENDAR_ITEM_COLORS.invoice_due,
    icon: CALENDAR_ITEM_ICONS.invoice_due,
    invoiceData: invoice,
    customerName: invoice.Contact?.Name,
    amount: invoice.AmountDue,
    currencyCode: invoice.CurrencyCode,
  };
}

/**
 * Helper function to create a calendar item from an invoice creation date
 * Shows when an invoice was created (historical view)
 */
export function invoiceCreationToCalendarItem(invoice: XeroInvoice): UnifiedCalendarItem | null {
  const creationDate = invoice.Date || invoice.DateString;
  if (!creationDate) return null;
  
  const parsedDate = parseXeroDateForCalendar(creationDate);
  if (!parsedDate) return null;
  
  const formattedAmount = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: invoice.CurrencyCode || 'AUD',
  }).format(invoice.Total);
  
  return {
    id: `invoice-created-${invoice.InvoiceID}`,
    type: 'invoice_created',
    title: `Created: ${invoice.InvoiceNumber} - ${invoice.Contact?.Name || 'Unknown'} (${formattedAmount})`,
    start: parsedDate.toISOString(),
    allDay: true,
    color: CALENDAR_ITEM_COLORS.invoice_created,
    icon: CALENDAR_ITEM_ICONS.invoice_created,
    invoiceData: invoice,
    customerName: invoice.Contact?.Name,
    amount: invoice.Total,
    currencyCode: invoice.CurrencyCode,
    description: `Invoice created on ${parsedDate.toLocaleDateString()}`,
  };
}

/**
 * Helper function to create a calendar item from a bill (expense) due date
 * Bills are ACCPAY type documents in Xero - actual expenses that were recorded
 */
export function billToCalendarItem(bill: XeroInvoice): UnifiedCalendarItem | null {
  const dueDate = bill.DueDate || bill.DueDateString;
  if (!dueDate) return null;
  
  const parsedDate = parseXeroDateForCalendar(dueDate);
  if (!parsedDate) return null;
  
  const formattedAmount = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: bill.CurrencyCode || 'AUD',
  }).format(bill.Total);
  
  return {
    id: `bill-${bill.InvoiceID}`,
    type: 'bill_due',
    title: `Bill: ${bill.Contact?.Name || 'Unknown'} (${formattedAmount})`,
    start: parsedDate.toISOString(),
    allDay: true,
    color: CALENDAR_ITEM_COLORS.bill_due,
    icon: CALENDAR_ITEM_ICONS.bill_due,
    invoiceData: bill, // Reuse invoiceData field since structure is identical
    customerName: bill.Contact?.Name,
    amount: bill.Total,
    currencyCode: bill.CurrencyCode,
    description: bill.Reference || `Bill #${bill.InvoiceNumber}`,
  };
}

/**
 * Statuses that mean a job is finished and should NOT be plotted.
 * Copied verbatim from TodayTimeline so the two surfaces cannot disagree about
 * what "still open" means — they read the same useAppData() jobs array.
 */
const CLOSED_JOB_STATUSES: string[] = ['Closed', 'Cancelled', 'Archived'];

/**
 * Helper function to create a calendar item from a job's due date.
 *
 * Uses `job.dueDate` and the same open/closed test as TodayTimeline. Returns
 * null for closed jobs and for jobs with no usable due date.
 */
export function jobToCalendarItem(job: Job): UnifiedCalendarItem | null {
  if (!job) return null;
  if (CLOSED_JOB_STATUSES.includes(job.status as string)) return null;

  // Older job documents can hold a Firestore Timestamp here even though the
  // interface says string, so accept .toDate() the way TodayTimeline's safeDate
  // does before falling through to the canonical parser.
  const raw: any = job.dueDate;
  let parsedDate: Date | null = null;
  if (raw && typeof raw.toDate === 'function') {
    try {
      const d = raw.toDate();
      parsedDate = isNaN(d.getTime()) ? null : d;
    } catch {
      parsedDate = null;
    }
  } else {
    parsedDate = parseXeroDateForCalendar(raw);
  }
  if (!parsedDate) return null;

  return {
    id: `job-${job.id}`,
    type: 'job_due',
    title: `Job: ${job.jobLabel || '(untitled job)'}`,
    start: parsedDate.toISOString(),
    allDay: true,
    color: CALENDAR_ITEM_COLORS.job_due,
    icon: CALENDAR_ITEM_ICONS.job_due,
    jobData: job,
    customerId: job.customerId?.[0],
    description: job.description,
  };
}

/**
 * Helper function to create calendar items from a native repeating invoice template
 * Generates occurrences for the specified date range
 * Differentiates between invoice (income) and bill (expense) via templateType
 */
export function repeatingInvoiceToCalendarItems(
  repeating: RepeatingInvoice,
  rangeStart: Date,
  rangeEnd: Date
): UnifiedCalendarItem[] {
  const items: UnifiedCalendarItem[] = [];
  if (repeating.status !== 'ACTIVE' || !repeating.nextDate) return items;

  // Everything below walks the occurrence cursor in 'YYYY-MM-DD' space. Two bugs
  // lived in the old Date-object version: an inline setMonth() switch that rolled
  // Jan-31 to Mar-3 (skipping February entirely), and Date comparisons against the
  // window/end bounds that silently dropped the FINAL occurrence when the range
  // straddled an ACDT->AEST change. Strings compare lexicographically = calendar
  // order, with no offset to get wrong.
  const firstDay = toDayString(repeating.nextDate);
  if (!firstDay) return items;

  const isExpense = repeating.templateType === 'bill';
  const itemType: CalendarItemType = isExpense ? 'recurring_expense' : 'recurring_invoice';
  const itemColor = isExpense ? CALENDAR_ITEM_COLORS.recurring_expense : CALENDAR_ITEM_COLORS.recurring_invoice;
  const itemIcon = isExpense ? CALENDAR_ITEM_ICONS.recurring_expense : CALENDAR_ITEM_ICONS.recurring_invoice;
  const itemLabel = isExpense ? 'Expense' : 'Invoice';

  // Template total: use stored total when present, else derive from lineItems (lineAmount or quantity * unitAmount)
  const total =
    typeof repeating.total === 'number' && !isNaN(repeating.total)
      ? repeating.total
      : (repeating.lineItems || []).reduce(
          (sum, li) => sum + (li.lineAmount ?? (li.quantity ?? 1) * (li.unitAmount ?? 0)),
          0
        );
  const currencyCode = 'AUD';
  const formattedAmount = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: currencyCode,
  }).format(total);

  // The anchor day-of-month comes from the template's startDate, exactly as the
  // backend scheduler does it — that is what makes a 31st template come back to
  // the 31st after February instead of permanently drifting down to the 28th.
  const anchorDay = toDayString(repeating.scheduleAnchorDate) || toDayString(repeating.startDate) || firstDay;
  const endDay = repeating.endDate ? toDayString(repeating.endDate) : null;
  const rangeStartDay = toLocalDateString(rangeStart);
  const rangeEndDay = toLocalDateString(rangeEnd);

  let current = firstDay;
  const maxOccurrences = 50;
  let count = 0;

  while (current <= rangeEndDay && count < maxOccurrences) {
    if (endDay && current > endDay) break;
    if (current >= rangeStartDay) {
      const iso = dayToIso(current);
      items.push({
        id: `recurring-${repeating.id}-${iso}`,
        type: itemType,
        title: `${itemLabel}: ${repeating.contactName || 'Unknown'} (${formattedAmount})`,
        start: iso,
        allDay: true,
        color: itemColor,
        icon: itemIcon,
        repeatingInvoiceData: repeating,
        customerName: repeating.contactName ?? undefined,
        amount: total,
        currencyCode,
        occurrenceDate: iso,
        isOccurrence: true,
        description: repeating.reference ?? undefined,
      });
    }

    let next: string | null = null;
    try {
      next = addFrequency(current, repeating.frequency, anchorDay);
    } catch {
      // Unknown frequency: the backend pauses the template rather than guessing.
      // The calendar's equivalent is to stop predicting, not to invent monthly
      // occurrences the scheduler will never actually bill.
      next = null;
    }
    if (!next || next <= current) break; // also guards a non-advancing cursor
    current = next;
    count++;
  }

  return items;
}
